// レシート一覧の本体
//
// 件数が増え続けるので、下端が近づいたら次のページを取りつつ行を仮想化する。
import { useVirtualizer } from '@tanstack/react-virtual'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'

import type { ReceiptSummary } from '@/api/types/receipt'

import { ReceiptRow } from './receipt-row'

/** 下端からこの距離まで来たら次を読みに行く */
const LOAD_MORE_MARGIN_PX = 600

type Props = {
  receipts: ReceiptSummary[]
  selectedId: number | null
  onSelect: (receiptId: number) => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}

export const ReceiptList = ({ receipts, selectedId, onSelect, hasNextPage, isFetchingNextPage, onLoadMore }: Props) => {
  'use no memo' // TanStack Virtual は React Compiler と非互換なため memoization をスキップ
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual は React Compiler 不対応、上記 'use no memo' で memoize 除外済み
  const virtualizer = useVirtualizer({
    count: receipts.length,
    getScrollElement: () => scrollRef.current,
    // バッジの折り返しで高さが変わるので、描画後に measureElement で測り直す
    estimateSize: () => 76,
    overscan: 8,
    gap: 8,
  })

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    onLoadMore()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !scrollRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore()
      },
      { root: scrollRef.current, rootMargin: `${LOAD_MORE_MARGIN_PX}px` },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  const items = virtualizer.getVirtualItems()

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((item) => {
          const receipt = receipts.at(item.index)
          if (!receipt) return null
          return (
            <div
              key={receipt.id}
              ref={virtualizer.measureElement}
              data-index={item.index}
              className="absolute top-0 left-0 w-full"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              <ReceiptRow receipt={receipt} isSelected={receipt.id === selectedId} onSelect={onSelect} />
            </div>
          )
        })}
      </div>

      <div ref={sentinelRef} className="h-px" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-3 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}
    </div>
  )
}
